import React, { useState, useEffect, useMemo } from 'react';
import { Form, Button, Badge, Modal, ButtonGroup, Spinner } from 'react-bootstrap';
import { BookA, Search, Plus, Pencil, AlertTriangle } from 'lucide-react';
import axios from '../../../services/AxiosInstance';
import { getApiUrl } from '../../../utils/ApiUtils';
import { toast } from 'sonner';
import { COMPANY_TYPE_TO_SHORT_CODE, toShortCode } from '../../../constants/companyTypes';

// Canonical short codes for the accounts.applicable_company_types SET column.
// Order here drives the badge / checkbox order in the UI.
const COMPANY_TYPE_CODES = ['AB', 'EF', 'HB', 'KB', 'EK', 'IF', 'ST'];
const SHORT_CODE_TO_LABEL = Object.entries(COMPANY_TYPE_TO_SHORT_CODE).reduce(
    (acc, [label, code]) => { acc[code] = label; return acc; },
    {}
);

// Parse the SET column (e.g. "AB,EF,HB") into a sorted array.
// A null/missing value is treated as "applies to every company type" so legacy
// rows that pre-date migration 02 don't suddenly disappear from the view.
function parseApplicableTypes(raw) {
    if (raw === null || raw === undefined || raw === '') return [...COMPANY_TYPE_CODES];
    if (Array.isArray(raw)) return raw;
    return raw.split(',').map(s => s.trim()).filter(Boolean);
}

function isUniversalSet(codes) {
    return COMPANY_TYPE_CODES.every(c => codes.includes(c));
}

const VAT_MAPPING_OPTIONS = [
    { value: '', label: 'Ingen' },
    { value: '05', label: '05 - Momspliktig försäljning' },
    { value: '07', label: '07 - Uttag' },
    { value: '08', label: '08 - Hyresinkomst frivillig skattskyldighet' },
    { value: '10', label: '10 - Utgående moms 25%' },
    { value: '11', label: '11 - Utgående moms 12%' },
    { value: '12', label: '12 - Utgående moms 6%' },
    { value: '20', label: '20 - Inköp varor annat EU-land' },
    { value: '21', label: '21 - Inköp tjänster annat EU-land' },
    { value: '22', label: '22 - Inköp tjänst utanför EU' },
    { value: '23', label: '23 - Inköp varor i Sverige (omvänd)' },
    { value: '24', label: '24 - Inköp tjänst i Sverige (omvänd)' },
    { value: '30', label: '30 - Utgående moms 25% (omvänd)' },
    { value: '31', label: '31 - Utgående moms 12% (omvänd)' },
    { value: '32', label: '32 - Utgående moms 6% (omvänd)' },
    { value: '35', label: '35 - Försäljning varor till EU' },
    { value: '36', label: '36 - Försäljning varor utanför EU' },
    { value: '37', label: '37 - Mellanmans inköp trepartshandel' },
    { value: '38', label: '38 - Mellanmans försäljning trepartshandel' },
    { value: '39', label: '39 - Försäljning tjänster till EU (huvudregel)' },
    { value: '40', label: '40 - Försäljning tjänster utanför EU' },
    { value: '41', label: '41 - Övrig försäljning utanför EU' },
    { value: '42', label: '42 - Momsfri försäljning' },
    { value: '48', label: '48 - Ingående moms' },
    { value: '50', label: '50 - Importmoms' },
];

function AccountPlanManager({ companyId, companyType }) {
    const tenantShortCode = useMemo(() => toShortCode(companyType), [companyType]);
    const tenantTypeLabel = companyType || 'detta bolag';

    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [planFilter, setPlanFilter] = useState('all');
    // Filter on by default — T05 / T06 / T07 expect non-applicable rows hidden
    // out of the box. Toggling off reveals every row plus its company-type
    // badges, which is the escape hatch for SIE-imported / mis-tagged accounts.
    const [onlyApplicable, setOnlyApplicable] = useState(true);
    const [editingAccount, setEditingAccount] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [newAccount, setNewAccount] = useState({
        accountNumber: '', accountName: '', SRU: '', VAT_mapping: '', baseAccountId: ''
    });

    const fetchAccounts = async () => {
        try {
            const response = await axios.get(
                getApiUrl(`${companyId}/accounting/accounts/full`),
                { withCredentials: true }
            );
            if (response.data.success) {
                setAccounts(response.data.accounts);
            }
        } catch (error) {
            console.error('Error fetching accounts:', error);
            toast.error('Kunde inte hämta kontoplanen');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAccounts();
    }, [companyId]);

    const filteredAccounts = useMemo(() => {
        return accounts.filter(account => {
            if (planFilter === 'base' && !account.is_base_plan) return false;
            if (planFilter === 'custom' && account.is_base_plan) return false;
            if (onlyApplicable && tenantShortCode) {
                const codes = parseApplicableTypes(account.applicable_company_types);
                if (!codes.includes(tenantShortCode)) return false;
            }
            if (search) {
                const s = search.toLowerCase();
                return account.account_number.includes(s) ||
                    (account.account_name && account.account_name.toLowerCase().includes(s));
            }
            return true;
        });
    }, [accounts, search, planFilter, onlyApplicable, tenantShortCode]);

    const handleSaveEdit = async () => {
        const editedTypes = parseApplicableTypes(editingAccount.applicable_company_types);
        if (editedTypes.length === 0) {
            toast.error('Minst en bolagstyp måste vara markerad');
            return;
        }
        setSaving(true);
        try {
            const response = await axios.put(
                getApiUrl(`${companyId}/accounting/accounts/${editingAccount.account_id}`),
                {
                    accountName: editingAccount.account_name,
                    SRU: editingAccount.SRU || '',
                    VAT_mapping: editingAccount.VAT_mapping || '',
                    baseAccountId: editingAccount.base_account_id || '',
                    applicableCompanyTypes: editedTypes
                },
                { withCredentials: true }
            );
            if (response.data.success) {
                toast.success('Kontot har uppdaterats');
                setEditingAccount(null);
                fetchAccounts();
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Kunde inte uppdatera kontot');
        } finally {
            setSaving(false);
        }
    };

    const toggleEditingType = (code) => {
        const current = parseApplicableTypes(editingAccount.applicable_company_types);
        const next = current.includes(code)
            ? current.filter(c => c !== code)
            : [...current, code];
        setEditingAccount({ ...editingAccount, applicable_company_types: next.join(',') });
    };

    const handleCreateAccount = async () => {
        setSaving(true);
        try {
            const response = await axios.post(
                getApiUrl(`${companyId}/accounting/accounts`),
                newAccount,
                { withCredentials: true }
            );
            if (response.data.success) {
                toast.success('Kontot har skapats');
                setShowAddModal(false);
                setNewAccount({ accountNumber: '', accountName: '', SRU: '', VAT_mapping: '', baseAccountId: '' });
                fetchAccounts();
            }
        } catch (error) {
            const msg = error.response?.data?.message || 'Kunde inte skapa kontot';
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    const customCount = accounts.filter(a => !a.is_base_plan).length;

    if (loading) {
        return (
            <div className="modern-card">
                <div className="modern-card-body text-center py-5">
                    <Spinner animation="border" className="text-primary" />
                    <p className="text-muted mt-2">Laddar kontoplan...</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="modern-card">
                <div className="modern-card-header">
                    <div>
                        <h2 className="section-title">
                            <BookA size={24} />
                            Kontoplan
                        </h2>
                        <p className="section-subtitle">
                            {accounts.length} konton totalt
                            {customCount > 0 && ` — ${customCount} egna konton`}
                        </p>
                    </div>
                    <Button variant="primary" size="sm" onClick={() => setShowAddModal(true)}>
                        <Plus size={16} className="me-1" />
                        Lägg till konto
                    </Button>
                </div>

                <div className="modern-card-body">
                    <div className="d-flex gap-3 mb-3 align-items-center flex-wrap">
                <div className="position-relative" style={{ flex: '1 1 250px', maxWidth: '350px' }}>
                    <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                    <Form.Control
                        type="text"
                        placeholder="Sök konto..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        style={{ paddingLeft: '36px' }}
                    />
                </div>
                <ButtonGroup size="sm">
                    <Button variant={planFilter === 'all' ? 'primary' : 'outline-secondary'} onClick={() => setPlanFilter('all')}>
                        Alla
                    </Button>
                    <Button variant={planFilter === 'base' ? 'primary' : 'outline-secondary'} onClick={() => setPlanFilter('base')}>
                        Basplan
                    </Button>
                    <Button variant={planFilter === 'custom' ? 'primary' : 'outline-secondary'} onClick={() => setPlanFilter('custom')}>
                        Egna konton
                        {customCount > 0 && <Badge bg="warning" className="ms-1">{customCount}</Badge>}
                    </Button>
                </ButtonGroup>
                {tenantShortCode && (
                    <Form.Check
                        type="switch"
                        id="only-applicable-switch"
                        className="ms-1"
                        checked={onlyApplicable}
                        onChange={e => setOnlyApplicable(e.target.checked)}
                        label={`Bara konton för ${tenantTypeLabel}`}
                        title="När av syns alla konton i tenanten — använd för att redigera bolagstyps-taggar på SIE-importerade eller egna konton"
                    />
                )}
            </div>

            <style>{`
                .account-table-scroll {
                    max-height: 600px;
                    overflow-y: auto;
                    border: 1px solid #e5e7eb;
                    border-radius: 6px;
                }
                .account-table-scroll table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 0.875rem;
                    margin-bottom: 0;
                }
                .account-table-scroll th {
                    background: #f8fafc;
                    position: sticky;
                    top: 0;
                    z-index: 10;
                    padding: 0.6rem 0.75rem;
                    border-bottom: 2px solid #e5e7eb;
                    font-weight: 600;
                    color: #1e293b;
                    font-size: 0.8rem;
                }
                .account-table-scroll td {
                    padding: 0.4rem 0.75rem;
                    border-bottom: 1px solid #f1f5f9;
                    vertical-align: middle;
                }
                .account-table-scroll tr:hover td {
                    background-color: #f8fafc;
                }
            `}</style>
            <div className="account-table-scroll">
                <table>
                    <thead>
                                <tr>
                                    <th style={{ width: '80px' }}>Konto</th>
                                    <th>Kontonamn</th>
                                    <th style={{ width: '100px' }}>SRU</th>
                                    <th style={{ width: '80px' }}>Moms</th>
                                    <th style={{ width: '180px' }}>Tillämplig för</th>
                                    <th style={{ width: '100px' }}>Status</th>
                                    <th style={{ width: '50px' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredAccounts.map(account => {
                                    const codes = parseApplicableTypes(account.applicable_company_types);
                                    const universal = isUniversalSet(codes);
                                    return (
                                    <tr key={account.account_id} style={{ opacity: account.is_base_plan ? 0.85 : 1 }}>
                                        <td><strong>{account.account_number}</strong></td>
                                        <td>{account.account_name}</td>
                                        <td><span className="text-muted">{account.SRU || '—'}</span></td>
                                        <td><span className="text-muted">{account.VAT_mapping || '—'}</span></td>
                                        <td>
                                            {universal ? (
                                                <span className="text-muted" style={{ fontSize: '0.75rem' }}>alla bolagstyper</span>
                                            ) : (
                                                codes.map(code => (
                                                    <Badge
                                                        key={code}
                                                        bg={code === tenantShortCode ? 'primary' : 'secondary'}
                                                        className="me-1"
                                                        style={{ fontSize: '0.7rem' }}
                                                        title={SHORT_CODE_TO_LABEL[code] || code}
                                                    >
                                                        {code}
                                                    </Badge>
                                                ))
                                            )}
                                        </td>
                                        <td>
                                            {!account.is_base_plan && (
                                                <>
                                                    <Badge bg="info" className="me-1" style={{ fontSize: '0.7rem' }}>Eget</Badge>
                                                    {!account.VAT_mapping && !account.SRU && (
                                                        <AlertTriangle size={14} className="text-warning" title="Saknar moms- och SRU-mappning" />
                                                    )}
                                                </>
                                            )}
                                        </td>
                                        <td>
                                            <Button
                                                variant="link"
                                                size="sm"
                                                className="p-0"
                                                onClick={() => setEditingAccount({ ...account })}
                                            >
                                                <Pencil size={14} />
                                            </Button>
                                        </td>
                                    </tr>
                                    );
                                })}
                                {filteredAccounts.length === 0 && (
                                    <tr>
                                        <td colSpan={7} className="text-center text-muted py-4">
                                            Inga konton matchar sökningen
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                </table>
                    </div>
                </div>
            </div>

            {/* Edit Modal */}
            <Modal show={!!editingAccount} onHide={() => setEditingAccount(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Redigera konto {editingAccount?.account_number}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {editingAccount && (
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Label>Kontonamn</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={editingAccount.account_name || ''}
                                    onChange={e => setEditingAccount({ ...editingAccount, account_name: e.target.value })}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>SRU-kod</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={editingAccount.SRU || ''}
                                    onChange={e => setEditingAccount({ ...editingAccount, SRU: e.target.value })}
                                    placeholder="T.ex. 7410"
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Momskod (VAT-mappning)</Form.Label>
                                <Form.Select
                                    value={editingAccount.VAT_mapping || ''}
                                    onChange={e => setEditingAccount({ ...editingAccount, VAT_mapping: e.target.value })}
                                >
                                    {VAT_MAPPING_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Tillämplig för bolagstyper</Form.Label>
                                <div className="d-flex flex-wrap gap-3">
                                    {COMPANY_TYPE_CODES.map(code => {
                                        const checked = parseApplicableTypes(editingAccount.applicable_company_types).includes(code);
                                        return (
                                            <Form.Check
                                                key={code}
                                                type="checkbox"
                                                id={`applicable-${code}`}
                                                checked={checked}
                                                onChange={() => toggleEditingType(code)}
                                                label={`${code} — ${SHORT_CODE_TO_LABEL[code] || code}`}
                                            />
                                        );
                                    })}
                                </div>
                                <Form.Text className="text-muted">
                                    Styr om kontot visas i kontoplanen för respektive bolagstyp. Påverkar även SIE-export och momsrapport.
                                </Form.Text>
                            </Form.Group>
                        </Form>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setEditingAccount(null)}>Avbryt</Button>
                    <Button
                        variant="primary"
                        onClick={handleSaveEdit}
                        disabled={
                            saving ||
                            !editingAccount ||
                            parseApplicableTypes(editingAccount.applicable_company_types).length === 0
                        }
                    >
                        {saving ? <Spinner animation="border" size="sm" /> : 'Spara'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Add Modal */}
            <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Lägg till konto</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Kontonummer</Form.Label>
                            <Form.Control
                                type="text"
                                maxLength={4}
                                value={newAccount.accountNumber}
                                onChange={e => setNewAccount({ ...newAccount, accountNumber: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                                placeholder="T.ex. 3042"
                                isInvalid={newAccount.accountNumber.length > 0 && newAccount.accountNumber.length < 4}
                            />
                            <Form.Control.Feedback type="invalid">Måste vara exakt 4 siffror</Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Kontonamn</Form.Label>
                            <Form.Control
                                type="text"
                                value={newAccount.accountName}
                                onChange={e => setNewAccount({ ...newAccount, accountName: e.target.value })}
                                placeholder="T.ex. Försäljn tjänst 12% sv"
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>SRU-kod</Form.Label>
                            <Form.Control
                                type="text"
                                value={newAccount.SRU}
                                onChange={e => setNewAccount({ ...newAccount, SRU: e.target.value })}
                                placeholder="T.ex. 7410"
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Momskod (VAT-mappning)</Form.Label>
                            <Form.Select
                                value={newAccount.VAT_mapping}
                                onChange={e => setNewAccount({ ...newAccount, VAT_mapping: e.target.value })}
                            >
                                {VAT_MAPPING_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowAddModal(false)}>Avbryt</Button>
                    <Button
                        variant="primary"
                        onClick={handleCreateAccount}
                        disabled={saving || newAccount.accountNumber.length !== 4 || !newAccount.accountName.trim()}
                    >
                        {saving ? <Spinner animation="border" size="sm" /> : 'Skapa konto'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}

export default AccountPlanManager;
